/**
 * Chart palette — StickyIQ.
 *
 * =====================================================================
 *  NEMĚŇ TYTO HODNOTY BEZ SPUŠTĚNÍ VALIDÁTORU.
 * =====================================================================
 *
 * Paleta NENÍ vybraná od oka. Je odvozená metodou z dataviz skillu a ověřená
 * validátorem v obou režimech na `--pairs all` (heatmapy, scatter, small
 * multiples — tam může kterákoli dvojice barev sousedit):
 *
 *   node scripts/validate_palette.js "#eb6834,#2e86c8,#1baf7a" \
 *        --mode light --pairs all
 *   node scripts/validate_palette.js "#d95926,#4a97d8,#199e70" \
 *        --mode dark --surface "#1a1a19" --pairs all
 *
 * Výsledek (2026-07-20): ALL CHECKS PASS v obou režimech.
 *   light: nejhorší CVD ΔE 9.2 (deutan), normal-vision floor 19.3
 *   dark:  nejhorší CVD ΔE 9.4 (deutan), normal-vision floor 17.1
 *
 * Kotva: B = #2e86c8 — historická barva dodavatele B ze všech dosavadních
 * reportů (reference/memory.md). Ta se nemění, zbytek je dopočítaný kolem ní.
 *
 * ZAMÍTNUTÉ VARIANTY (ať to nikdo nezkouší znovu):
 *   - zelená #008300 pro C: proti oranžové A kolabuje pod protanopií (ΔE 3.2 FAIL)
 *   - fialová #4a3aa7 pro C: projde v light, ale v dark kolabuje proti modré B
 *     (ΔE 2.4 FAIL) — dark lightness band 0.48–0.67 je na modrou i fialovou úzký
 *   - magenta pro C: padá na normal-vision floor proti oranžové A (ΔE 12.9 FAIL)
 *   - čtvrtý plnohodnotný odstín: v dark neprojde `--pairs all` ŽÁDNÝ. Proto
 *     „ostatní/konkurence" jde přes neutrál + texturu (viz NEUTRAL_SERIES níže).
 *
 * ---------------------------------------------------------------------
 *  POVINNÝ RELIEF U DODAVATELE C (light) — TOHLE NENÍ DOPORUČENÍ
 * ---------------------------------------------------------------------
 * Light varianta C (#1baf7a) má kontrast vůči světlému podkladu 2.74:1, tedy
 * POD hranicí 3:1. Validátor ji pustí výhradně proto, že se k ní dodá „relief"
 * kanál — hodnota musí být čitelná i jinak než barvou:
 *
 *      přímé popisky u sérií  NEBO  vždy dostupný tabulkový pohled.
 *
 * Bez toho paleta přestává splňovat kontrastní gate. Protože „nezapomeň na
 * popisky" v dokumentaci nikdo za půl roku nenajde, je ten závazek vynucený
 * kódem: každý graf, který kreslí sérii C, musí projít `assertContrastRelief()`
 * (níže). V dev buildu to při porušení HODÍ VÝJIMKU, v produkci zaloguje chybu.
 * Když někdo popisky odstraní, spadne to hned, ne až u zákazníka.
 *
 * Kdyby ses tomu chtěl vyhnout: nesahej na `assertContrastRelief`, ale zvedni
 * light C nad 3:1 a znovu spusť validátor v OBOU režimech.
 */

export type SupplierKey = 'A' | 'B' | 'C' | 'other';
export type ThemeMode = 'light' | 'dark';

export interface SeriesColor {
  /** Barva v light režimu. */
  light: string;
  /** Barva v dark režimu. */
  dark: string;
  /** Kontrast vůči podkladu daného režimu (z validátoru). */
  contrast: { light: number; dark: number };
}

/** Podklady, proti kterým byla paleta validovaná. Musí sedět s tokens.css. */
export const CHART_SURFACE: Record<ThemeMode, string> = {
  light: '#fcfcfb',
  dark: '#1a1a19',
};

/** Hranice, pod kterou je nutný relief kanál (WCAG non-text contrast). */
export const CONTRAST_FLOOR = 3.0;

export const SUPPLIER_COLORS: Record<SupplierKey, SeriesColor> = {
  // Dodavatel A — group `generic` / `autorefresh`, pozice sticky*
  A: { light: '#eb6834', dark: '#d95926', contrast: { light: 3.21, dark: 4.62 } },
  // Dodavatel B — group `animTest*`, pozice fixed*. KOTVA z memory.md.
  B: { light: '#2e86c8', dark: '#4a97d8', contrast: { light: 3.62, dark: 5.11 } },
  // Dodavatel C — group `lwms` (POZOR: běží na doméně A, identifikace podle group)
  // Light varianta je pod 3:1 → viz assertContrastRelief().
  C: { light: '#1baf7a', dark: '#199e70', contrast: { light: 2.74, dark: 3.41 } },
  // „ostatní" — složeno do neutrálu podle pravidla „fold to Other".
  // NENÍ to plnohodnotná série: nemá chroma na identitu, vždy s přímým popiskem
  // a (kde to jde) se šrafou. Nikdy z ní nedělej čtvrtou barvu v legendě.
  other: { light: '#898781', dark: '#898781', contrast: { light: 3.54, dark: 4.02 } },
};

/**
 * Sloty, které v daném režimu NEsplní 3:1 samy o sobě a vyžadují relief.
 * Odvozeno z `contrast` výše, ne ručně vypsané — když se barva změní,
 * seznam se změní s ní.
 */
export function slotsNeedingRelief(mode: ThemeMode): SupplierKey[] {
  return (Object.keys(SUPPLIER_COLORS) as SupplierKey[]).filter(
    (k) => SUPPLIER_COLORS[k].contrast[mode] < CONTRAST_FLOOR,
  );
}

export interface ReliefDeclaration {
  /** Série mají přímé popisky přímo u marku (ne jen v legendě). */
  directLabels?: boolean;
  /** K grafu je vždy dostupný tabulkový pohled se stejnými čísly. */
  tableFallback?: boolean;
}

export class ContrastReliefError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ContrastReliefError';
  }
}

/**
 * Vynucení relief kanálu. Volá se z každého grafu, který kreslí série dodavatelů.
 *
 * Legenda sama o sobě relief NENÍ — čtenář musí spárovat barvu s položkou
 * legendy, a přesně to je u sub-3:1 barvy nespolehlivé. Proto se uznává jen
 * přímý popisek u marku nebo tabulkový pohled.
 */
export function assertContrastRelief(
  chartName: string,
  mode: ThemeMode,
  suppliers: readonly SupplierKey[],
  relief: ReliefDeclaration,
): void {
  const risky = slotsNeedingRelief(mode).filter((s) => suppliers.includes(s));
  if (risky.length === 0) return;

  const hasRelief = Boolean(relief.directLabels || relief.tableFallback);
  if (hasRelief) return;

  const detail = risky
    .map((s) => `${s} (${SUPPLIER_COLORS[s][mode]}, kontrast ${SUPPLIER_COLORS[s].contrast[mode]}:1)`)
    .join(', ');

  const message =
    `[${chartName}] Graf kreslí sérii s kontrastem pod ${CONTRAST_FLOOR}:1 — ${detail} — ` +
    `bez relief kanálu. Doplň přímé popisky u sérií (directLabels) nebo tabulkový ` +
    `pohled (tableFallback). Viz komentář v src/charts/palette.ts. ` +
    `Bez toho paleta nesplňuje kontrastní gate a validátor by ji nepustil.`;

  if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
    throw new ContrastReliefError(message);
  }
  // V produkci graf nezabíjíme, ale nesmí to zapadnout.
  console.error(message);
}

export function supplierColor(supplier: SupplierKey, mode: ThemeMode): string {
  return (SUPPLIER_COLORS[supplier] ?? SUPPLIER_COLORS.other)[mode];
}

/**
 * Dodavatel z API přichází jako volný `string` (backend ho klasifikuje podle
 * `group`). Tohle je jediné místo, kde se zúží na známý slot — neznámá hodnota
 * spadne do „ostatní" místo do nedefinované barvy.
 */
export function toSupplierKey(supplier: string | null | undefined): SupplierKey {
  return supplier === 'A' || supplier === 'B' || supplier === 'C' ? supplier : 'other';
}

/** Popisek dodavatele do legend a tooltipů (UI je česky). */
export const SUPPLIER_LABEL: Record<SupplierKey, string> = {
  A: 'Dodavatel A',
  B: 'Dodavatel B',
  C: 'Dodavatel C',
  other: 'Ostatní',
};

/**
 * Neutrální série pro „konkurence" v guard modulu (04) a pro bucket „ostatní".
 * Šrafa je tu jako druhý kanál (přístupnost), ne dekorace.
 */
export const NEUTRAL_SERIES = {
  color: SUPPLIER_COLORS.other,
  /** ECharts decal — 45° šrafa, viz `decal` na sérii. */
  decal: {
    symbol: 'rect',
    dashArrayX: [1, 0],
    dashArrayY: [2, 5],
    symbolSize: 1,
    rotation: Math.PI / 4,
    color: 'rgba(0, 0, 0, 0.25)',
  },
} as const;

/**
 * Sekvenční rampa (magnitude) — jednoodstínová modrá 100→700.
 * Používá se pro heatmapy kliků a hodinové matice. Nejsvětlejší krok smí splynout
 * s podkladem, protože znamená „skoro nula".
 */
export const SEQUENTIAL_BLUE = [
  '#cde2fb', '#b7d3f6', '#9ec5f4', '#86b6ef', '#6da7ec',
  '#5598e7', '#3987e5', '#2a78d6', '#256abf', '#1c5cab',
  '#184f95', '#104281', '#0d366b',
] as const;

/**
 * Divergentní pár pro „vs benchmark" / „vs minulé období" (modrá ↔ červená).
 * Neutrální střed je šedá — musí číst jako „nic", ne jako třetí kategorie.
 */
export const DIVERGING = {
  negative: { light: '#d03b3b', dark: '#e66767' },
  neutral: { light: '#f0efec', dark: '#383835' },
  positive: { light: '#2a78d6', dark: '#3987e5' },
} as const;

/**
 * Status paleta — FIXNÍ, nikdy se netématizuje a nikdy se nemíchá se sériemi.
 * Vždy s ikonou + popiskem: warning a serious jsou na světlém podkladu pod 3:1
 * záměrně a barva sama nesmí nést význam.
 */
export const STATUS_COLORS = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
} as const;

export type StatusKey = keyof typeof STATUS_COLORS;

/** Chrome grafu — osy, mřížka, inkoust. */
export const CHART_INK: Record<ThemeMode, Record<string, string>> = {
  light: {
    surface: '#fcfcfb',
    textPrimary: '#0b0b0b',
    textSecondary: '#52514e',
    muted: '#898781',
    gridline: '#e1e0d9',
    axis: '#c3c2b7',
  },
  dark: {
    surface: '#1a1a19',
    textPrimary: '#ffffff',
    textSecondary: '#c3c2b7',
    muted: '#898781',
    gridline: '#2c2c2a',
    axis: '#383835',
  },
};
